import React, { useEffect } from 'react';
import { useSearchParams, useLocation, Link } from 'react-router-dom';
import { CheckCircleIcon, XCircleIcon, ClockIcon } from '@heroicons/react/24/outline';
import './booking-shared.css';
import './checkout.css';
import NotificationPrompt from '../../components/notifications/NotificationPrompt';

export default function CartConfirmation() {
  const [searchParams] = useSearchParams();
  const redirectStatus = searchParams.get('redirect_status');
  const bookingId = searchParams.get('bookingId');
  const shopOrderId = searchParams.get('shopOrderId');
  const paidCharges = searchParams.get('charges') === '1';
  // Real only on Stripe's return, or straight after a checkout that needed
  // no payment (the cart marks that). A bare visit to this address confirms
  // nothing and leaves the carts alone.
  const freeCheckout = useLocation().state?.freeCheckout === true && !redirectStatus;
  const succeeded = redirectStatus === 'succeeded' || freeCheckout;

  useEffect(() => {
    if (succeeded) {
      sessionStorage.removeItem('booking-cart');
      localStorage.removeItem('shopCart');
      window.dispatchEvent(new CustomEvent('booking-cart-update'));
      window.dispatchEvent(new CustomEvent('shop-cart-update'));
    }
  }, [succeeded]);

  if (redirectStatus && redirectStatus !== 'succeeded' && redirectStatus !== 'processing') {
    return (
      <div className="bk-page bk-page--narrow checkout-done">
        <span className="checkout-done__icon checkout-done__icon--bad"><XCircleIcon aria-hidden="true" /></span>
        <h1 className="checkout-done__title">Payment failed</h1>
        <p className="checkout-done__text">Your payment couldn't be processed. Nothing has been charged.</p>
        <Link to="/booking/cart" className="bk-link">Back to cart</Link>
      </div>
    );
  }

  if (redirectStatus === 'processing') {
    return (
      <div className="bk-page bk-page--narrow checkout-done">
        <span className="checkout-done__icon checkout-done__icon--wait"><ClockIcon aria-hidden="true" /></span>
        <h1 className="checkout-done__title">Payment processing</h1>
        <p className="checkout-done__text">Your payment is being processed. Check My bookings and My orders shortly.</p>
        <div className="checkout-done__links">
          {bookingId && <Link to="/booking/my-bookings" className="bk-link">My bookings</Link>}
          {shopOrderId && <Link to="/booking/my-orders" className="bk-link">My orders</Link>}
        </div>
      </div>
    );
  }

  if (!succeeded) {
    return (
      <div className="bk-page bk-page--narrow checkout-done">
        <h1 className="checkout-done__title">Nothing to confirm</h1>
        <p className="checkout-done__text">There is no new payment to confirm on this page. Your bookings and orders are in your account.</p>
        <div className="checkout-done__links">
          <Link to="/booking/my-bookings" className="bk-link">My bookings</Link>
          <Link to="/booking/my-orders" className="bk-link">My orders</Link>
          <Link to="/booking/cart" className="bk-link">Cart</Link>
        </div>
      </div>
    );
  }

  const hasBooking = !!bookingId;
  const hasShop = !!shopOrderId;
  // Outstanding charges paid on their own, or alongside a booking or kit
  const message = [
    hasBooking && 'Your session is booked.',
    hasShop && "Your kit order has been placed and we'll let you know when it arrives at the club.",
    paidCharges && 'Your charges have been paid.',
  ].filter(Boolean).join(' ') || 'Payment received.';
  const title = hasBooking && !hasShop && !paidCharges ? 'Booking confirmed!'
    : hasShop && !hasBooking && !paidCharges ? 'Order placed!'
    : !hasBooking && !hasShop ? 'Payment received!'
    : 'All confirmed!';

  return (
    <div className="bk-page bk-page--narrow checkout-done">
      <span className="checkout-done__icon"><CheckCircleIcon aria-hidden="true" /></span>
      {/* Only when a session was booked, not for charges or kit alone */}
      {hasBooking && <NotificationPrompt place="afterBooking" />}
      <h1 className="checkout-done__title">{title}</h1>
      <p>
        {message}{hasBooking ? ' See you on the trampoline!' : ''}
      </p>
      <div className="checkout-done__links">
        {hasBooking && <Link to="/booking/my-bookings" className="bk-link">My bookings</Link>}
        {hasShop && <Link to="/booking/my-orders" className="bk-link">My orders</Link>}
        {paidCharges && <Link to="/booking/my-account" className="bk-link">My Account</Link>}
        <Link to="/booking" className="bk-link">Back to calendar</Link>
      </div>
    </div>
  );
}
